import axios from 'axios';
import React, { useState, useEffect } from 'react';
import config from '../../config';
import { FaSpinner, FaImage, FaTimes } from "react-icons/fa";
import { ToastContainer, toast, Bounce } from "react-toastify";
import { useNavigate } from "react-router-dom";

const generateGuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

const defaultQuestion = {
    id: null,
    qCode: '',
    title: '',
    explain: '',
    videoId: '',
    options: [],
    tags: []
};

const QuestionForm = ({ initialQuestion, onSubmit, onCancel }) => {

    const [question, setQuestion] = useState(initialQuestion || defaultQuestion);
    const [correctOptionId, setCorrectOptionId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const [tagInput, setTagInput] = useState("");
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        const questionToEdit = initialQuestion || { ...defaultQuestion, id: generateGuid(), options: [], tags: [] };
        setQuestion(questionToEdit);
        
        const correctOpt = questionToEdit.options?.find(opt => opt.isCorrect);
        if (correctOpt) {
            setCorrectOptionId(correctOpt.id);
        } else {
            setCorrectOptionId(null);
        }
    }, [initialQuestion]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                toast.error(<p dir="rtl" className="font-custom">فقط فایل تصویری مجاز است</p>);
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                toast.error(<p dir="rtl" className="font-custom">حجم تصویر نباید بیشتر از ۵ مگابایت باشد</p>);
                return;
            }
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setQuestion(prev => ({ ...prev, [name]: value }));
    };

    const handleOptionTextChange = (id, newText) => {
        setQuestion(prev => ({
            ...prev,
            options: prev.options.map(opt =>
                opt.id === id ? { ...opt, text: newText } : opt
            ),
        }));
    };

    const handleCorrectOptionChange = (id) => {
        setCorrectOptionId(id);
        setQuestion(prev => ({
            ...prev,
            options: prev.options.map(opt => ({
                ...opt,
                isCorrect: opt.id === id,
            })),
        }));
    };

    const addOption = () => {
        const newOption = {
            id: generateGuid(),
            option: String.fromCharCode(65 + question.options.length), // A, B, C...
            text: '',
            isCorrect: false,
            questionId: question.id,
        };
        setQuestion(prev => ({
            ...prev,
            options: [...prev.options, newOption],
        }));
    };

    const removeOption = (id) => {
        setQuestion(prev => ({
            ...prev,
            options: prev.options.filter(opt => opt.id !== id),
        }));
        if (correctOptionId === id) {
            setCorrectOptionId(null);
        }
    };

    const handleSubmit = async (e) => {
        setIsLoading(true);
        e.preventDefault();
        if (question.options.length > 0 && !question.options.some(o => o.isCorrect)) {
            toast.error(<p dir="rtl" className="font-custom">لطفا یک گزینه صحیح را انتخاب کنید.</p>);
            setIsLoading(false);

            return;
        }

        const isUpdate = !!initialQuestion;
        const endpoint = isUpdate ? 'UpdateQuestion' : 'AddQuestion';
        const url = `${config.apiBaseUrl}/api/questions/${endpoint}`;

        const formData = new FormData();
        formData.append("id", isUpdate ? question.id : "");
        formData.append("title", question.title);
        formData.append("qCode", parseInt(question.qCode, 10));
        formData.append("explain", question.explain || "");
        formData.append("videoId", question.videoId || "");

        question.tags.forEach((tag, i) => {
            formData.append(`tags[${i}]`, tag);
        });

        question.options.forEach((opt, i) => {
            formData.append(`options[${i}].option`, opt.option);
            formData.append(`options[${i}].text`, opt.text);
            formData.append(`options[${i}].isCorrect`, opt.isCorrect);
        });

        if (selectedImage) {
            formData.append("image", selectedImage);
        }

        try {
            const response = await axios.post(url, formData, {
                headers: {
                    "HeaderKey": config.HeaderKey,
                },
                withCredentials: true,
            });

            if (onSubmit) {
                onSubmit(response.data);
            }
            let message = (isUpdate ? 'سوال با موفقیت به‌روزرسانی شد.' : 'سوال با موفقیت اضافه شد.');
            toast.success(<p dir="rtl" className="font-custom">{message}</p>);
            setTimeout(() => {
                navigate("/dashboard/questions");
            }, 300);

        } catch (error) {
            console.error(`Failed to ${isUpdate ? 'update' : 'add'} question:`, error);
            const errorMessage = error.response?.data?.message || error.response?.data || 'عملیات با خطا مواجه شد.';
            toast.error(<p dir="rtl" className="font-custom">{errorMessage}</p>);

        } finally {
            setIsLoading(false);
        }
    };

const handleTagChange = (e) => {
        const value = e.target.value;
        const separators = [",", "،"]; 

        if (separators.some(sep => value.includes(sep))) {
            const newTag = value.replace(/[,،]/g, "").trim();
            if (newTag && !question.tags.includes(newTag)) {
                setQuestion((prev) => ({
                    ...prev,
                    tags: [...prev.tags, newTag], 
                }));
            }
            setTagInput(""); 
        } else {
            setTagInput(value);
        }
    };

    const removeTag = (index) => {
        setQuestion((prev) => ({
            ...prev,
            tags: prev.tags.filter((_, i) => i !== index),
        }));
    };
    const baseInputClasses = "w-full p-2 box-border rounded border border-gray-300";
    const baseButtonClasses = "py-2 px-4 rounded cursor-pointer";

    return (
        <div dir="rtl" className="font-custom p-5 border border-gray-300 rounded-lg max-w-3xl my-5 mx-auto bg-gray-50">
            <form onSubmit={handleSubmit}>
                <h2 className="text-xl text-center font-bold mb-4">{initialQuestion ? 'ویرایش سوال' : 'ایجاد سوال جدید'}</h2>

                <div className="mb-4">
                    <label htmlFor="qCode" className="block mb-1 font-bold">کد سوال</label>
                    <div className="relative flex items-center">
                        <input
                            type="number"
                            id="qCode"
                            name="qCode"
                            value={question.qCode}
                            onChange={handleChange}
                            className={baseInputClasses}
                            required
                        />
                        <button
                            type="button"
                            title="دریافت کد بعدی"
                            className="absolute left-2 p-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs"
                            style={{ top: '50%', transform: 'translateY(-50%)' }}
                            onClick={async () => {
                                try {
                                    const response = await axios.get(
                                        `${config.apiBaseUrl}/api/questions/nextCode`,
                                        {
                                            headers: {
                                                "Content-Type": "application/json",
                                                HeaderKey: config.HeaderKey,
                                            },
                                            withCredentials: true,
                                        });
                                    const data = await response.data;
                                    setQuestion(prev => ({ ...prev, qCode: data }));
                                } catch (err) {
                                    alert('دریافت کد بعدی با خطا مواجه شد.');
                                }
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="mb-4">
                    <label htmlFor="title" className="block mb-1 font-bold">عنوان سوال</label>
                    <textarea id="title" name="title" value={question.title} onChange={handleChange} className={baseInputClasses} rows="3" required />
                </div>

                <div className="mb-4">
                    <label className="block mb-1 font-bold">گزینه‌ها</label>
                    {question.options.map((opt, index) => (
                        <div key={opt.id} className="flex items-center mb-2">
                            <input
                                type="radio"
                                name="correctOption"
                                checked={correctOptionId === opt.id}
                                onChange={() => handleCorrectOptionChange(opt.id)}
                                className="ml-2"
                            />
                            <span className="ml-2 font-bold">{String.fromCharCode(65 + index)}:</span>
                            <input
                                type="text"
                                value={opt.text}
                                onChange={(e) => handleOptionTextChange(opt.id, e.target.value)}
                                className={`${baseInputClasses} flex-grow`}
                                placeholder={`متن گزینه ${String.fromCharCode(65 + index)}`}
                                required
                            />
                            <button type="button" onClick={() => removeOption(opt.id)} className={`${baseButtonClasses} bg-red-600 text-white mr-2`}>حذف</button>
                        </div>
                    ))}
                    <button type="button" onClick={addOption} className={`${baseButtonClasses} bg-blue-600 text-white mt-2`}>افزودن گزینه</button>
                </div>

                <div>
                    <div className="p-3 border rounded-md w-full text-right">
                        <div className="flex flex-wrap gap-2 mb-2">
                            {question.tags.map((tag, index) => (
                                <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full flex items-center">
                                    {tag}
                                    <button
                                        type="button"
                                        onClick={() => removeTag(index)}
                                        className="ml-2 text-red-500 font-bold hover:text-red-700"
                                    >
                                        ✖
                                    </button>
                                </span>
                            ))}
                        </div>

                        <input
                            type="text"
                            name="tags"
                            placeholder="تگ‌ها را با کاما یا ویرگول جدا کنید"
                            value={tagInput}
                            onChange={handleTagChange}
                            className="p-2 border rounded-md w-full text-right focus:outline-none"
                            dir="rtl"
                        />
                    </div>
                </div>
                <div className="mb-4">
                    <label htmlFor="explain" className="block mb-1 font-bold">توضیحات (اختیاری)</label>
                    <textarea id="explain" name="explain" value={question.explain || ''} onChange={handleChange} className={baseInputClasses} rows="4" />
                </div>
                <div className="mb-4">
                    <label htmlFor="videoId" className="block mb-1 font-bold">شناسه یکتا فایل (اختیاری)</label>
                    <input type="text" id="videoId" name="videoId" value={question.videoId || ''} onChange={handleChange} className={`${baseInputClasses} dir-ltr`} />
                </div>

                <div className="mb-4">
                    <label className="block mb-1 font-bold">تصویر سوال (اختیاری)</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                        {imagePreview ? (
                            <div className="relative inline-block">
                                <img
                                    src={imagePreview}
                                    alt="پیش‌نمایش تصویر"
                                    className="max-w-full max-h-48 rounded-lg shadow-md mx-auto"
                                />
                                <button
                                    type="button"
                                    onClick={handleRemoveImage}
                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                >
                                    <FaTimes size={14} />
                                </button>
                                <p className="mt-2 text-sm text-green-600">{selectedImage?.name}</p>
                            </div>
                        ) : question.imagePath ? (
                            <div className="relative inline-block">
                                <img
                                    src={`${config.apiBaseUrl}${question.imagePath}`}
                                    alt="تصویر سوال"
                                    className="max-w-full max-h-48 rounded-lg shadow-md mx-auto"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'block';
                                    }}
                                />
                                <p className="text-red-500 hidden">خطا در بارگذاری تصویر</p>
                                <button
                                    type="button"
                                    onClick={handleRemoveImage}
                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                >
                                    <FaTimes size={14} />
                                </button>
                            </div>
                        ) : (
                            <label className="cursor-pointer block">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="hidden"
                                />
                                <FaImage className="mx-auto text-gray-400 mb-2" size={48} />
                                <p className="text-gray-500">کلیک کنید یا تصویر را بکشید</p>
                                <p className="text-xs text-gray-400 mt-1">PNG, JPG, GIF (حداکثر ۵ مگابایت)</p>
                            </label>
                        )}
                    </div>
                    {!imagePreview && !question.imagePath && (
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="mt-2"
                        />
                    )}
                </div>

                <div>
                    {/* <button type="submit" className={`${baseButtonClasses} bg-green-600 text-white mr-2`}>ذخیره</button> */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex justify-center items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg text-sm px-4 py-2.5 transition-colors duration-300 disabled:opacity-70"
                    >
                        {isLoading ? <FaSpinner className="animate-spin" /> : initialQuestion ? 'ویرایش سوال' : 'ایجاد سوال جدید'}
                    </button>
                    {/*onCancel && <button type="button" onClick={onCancel} className={`${baseButtonClasses} bg-red-600 text-white`}>انصراف</button>*/}
                </div>
            </form>
            <ToastContainer
                position="bottom-center"
                autoClose={5000}
                hideProgressBar={false}
                theme="dark"
                transition={Bounce}
                className={"rtl font-custom text-right"}
            />
        </div>
    );
};



export default QuestionForm;